package dev.lingvo.site.components.review

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.shared.QaIssueDto
import dev.lingvo.shared.QaSeverity
import dev.lingvo.shared.ReviewItemDto
import dev.lingvo.shared.TranslationDto
import dev.lingvo.site.components.source.validSourceSnapshot
import dev.lingvo.shared.TranslationOrigin
import dev.lingvo.shared.localeInfo
import dev.lingvo.site.components.editor.displayName
import dev.lingvo.site.ds.IconSize
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LBadge
import dev.lingvo.site.ds.LBadgeVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LButtonSize
import dev.lingvo.site.ds.LButtonVariant
import dev.lingvo.site.ds.LCard
import dev.lingvo.site.ds.LConflictCard
import dev.lingvo.site.ds.LDangerIcon
import dev.lingvo.site.ds.LDiffParts
import dev.lingvo.site.ds.LExternalLinkIcon
import dev.lingvo.site.ds.LLinkButton
import dev.lingvo.site.ds.LMuted
import dev.lingvo.site.ds.LWarningIcon
import dev.lingvo.site.ds.Radius
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.dateLong
import dev.lingvo.site.ds.iconSize
import dev.lingvo.site.ds.monoFont
import dev.lingvo.site.ds.toSitePalette
import dev.lingvo.site.ds.wordDiff
import dev.lingvo.site.i18n.t
import dev.lingvo.site.i18n.tf
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Everything needed to decide about one string, without opening another tab.
 *
 * **The submitted value carries the primacy.** Source and submission used to be two identical
 * blocks, so the eye had to read the labels to find out which one was the decision — on a surface
 * whose entire job is deciding about the second one. Submitted now sits on `surface` behind an
 * accent hairline at the inline start; Source is recessed onto `surfaceSunken`, present and exact
 * but visibly the reference rather than the subject.
 */
@Composable
internal fun ReviewCard(
    item: ReviewItemDto,
    slug: String,
    sourceLocale: String,
    targetLocale: String,
) {
    val palette = ColorMode.current.toSitePalette()
    LCard(Modifier.fillMaxWidth().attr("data-review-card", item.keyId)) {
        Column(Modifier.fillMaxWidth().gap(Space.LG)) {
            Row(
                Modifier.fillMaxWidth().gap(Space.SM),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Span(Modifier.monoFont().fontWeight(600).toAttrs()) { Text(item.keyName) }
                originBadge(item.origin)?.let { (text, variant) -> LBadge(text, variant = variant) }
                Spacer()
                // The escape hatch from a queue that shows one string at a time: everything else a
                // translator might want — history, comments, the neighbouring keys — lives in the
                // editor, and this is the one press that gets there without losing the key.
                LLinkButton(
                    label = t("review.review_card.open_in_editor", "Open in editor"),
                    path = "/app/projects/$slug/translate/${encodeURIComponent(targetLocale)}" +
                        "?key=${encodeURIComponent(item.keyId)}",
                    variant = LButtonVariant.GHOST,
                    size = LButtonSize.SM,
                    trailingIcon = { LExternalLinkIcon() },
                )
            }
            item.context?.takeIf(String::isNotBlank)?.let { LMuted(it) }

            ReviewValuePanel(
                label = t("review.review_card.source", "Source"),
                language = localeInfo(item.validSourceSnapshot()?.view?.sourceLocale ?: sourceLocale).englishName,
                ground = palette.surfaceSunken,
                accented = false,
            ) {
                LocalizedReviewValue(
                    locale = item.validSourceSnapshot()?.view?.sourceLocale ?: sourceLocale,
                    value = item.sourceValue,
                    pluralForms = item.sourcePluralForms,
                    hook = "source",
                )
            }

            ReviewValuePanel(
                label = t("review.review_card.submitted", "Submitted"),
                language = localeInfo(targetLocale).englishName,
                ground = palette.surface,
                accented = true,
            ) {
                LocalizedReviewValue(
                    locale = targetLocale,
                    value = item.targetValue,
                    pluralForms = item.targetPluralForms,
                    hook = "submitted",
                )
            }

            // The diff is supplemental. The submitted value above remains the exact, selectable
            // payload; splitting it into diff tokens must never become the only rendering because
            // that can hide the whitespace a reviewer is deciding about.
            if (item.targetValue != null && item.targetPluralForms == null) {
                val parts = item.previousApproved
                    ?.let { wordDiff(it, item.targetValue.orEmpty()) }
                    ?.takeIf { it.isNotEmpty() }
                if (parts != null) {
                    ReviewValuePanel(
                        label = t("review.review_card.changes_since_the_last_approval", "Changes since the last approval"),
                        language = null,
                        ground = palette.surfaceSunken,
                        accented = false,
                    ) {
                        // The design system's diff, not a local one. Additions and deletions retain
                        // their semantic <ins>/<del> markup for assistive technology.
                        LDiffParts(parts)
                    }
                }
            }

            item.submittedBy?.let { who ->
                // Two messages, not one message with a separator smuggled into an argument.
                // `"by {who}{when}"` handed a translator a sentence they could not touch: the
                // ` · ` lived in Kotlin, so it could not be moved, dropped or repunctuated, and
                // in an RTL locale it landed on the wrong side of the name.
                //
                // `dateLong`, not `submittedAt.take(10)`. "2026-09-03" is a machine's shape for
                // a date — read as prose it stops a reader mid-sentence — and every sibling date
                // on this surface is already formatted. A timestamp `dateLong` cannot parse falls
                // to the who-only sentence rather than putting the raw ISO back on the card.
                val submitted = item.submittedAt?.let(::dateLong)
                LMuted(
                    if (submitted == null) {
                        tf("review.review_card.by_who", "by {who}", "who" to who)
                    } else {
                        tf(
                            "review.review_card.by_who_when",
                            "by {who} · {when}",
                            "who" to who,
                            "when" to submitted,
                        )
                    },
                    Modifier.attr(
                        "data-review-attribution",
                        if (submitted == null) "who" else "who-when",
                    ),
                )
            }

            ReviewIssues(item.issues)

            if (item.comments.isNotEmpty()) {
                Column(Modifier.fillMaxWidth().gap(Space.XS)) {
                    Span(Modifier.fontWeight(600).fontSize(TypeScale.BodySmall).toAttrs()) {
                        Text(t("review.review_card.conversation", "Conversation"))
                    }
                    item.comments.forEach { comment -> LMuted(comment.body) }
                }
            }
        }
    }
}

/**
 * The QA verdict, at the weight each part of it deserves.
 *
 * **One alert, for the one thing that stops the decision.** Every issue used to be its own full
 * alert, so a string with four warnings and one error showed five equal blocks and the reviewer had
 * to read all of them to find out which one was blocking. The blocker keeps the alert; everything
 * else becomes a compact severity row, which is enough to say what QA thinks without competing with
 * the value above it.
 */
@Composable
private fun ReviewIssues(issues: List<QaIssueDto>) {
    if (issues.isEmpty()) return
    val palette = ColorMode.current.toSitePalette()
    val blocker = issues.firstOrNull { it.severity == QaSeverity.ERROR }
    val rest = issues.filter { it !== blocker }
    Column(
        Modifier.fillMaxWidth().gap(Space.XS).attr("data-review-issues", issues.size.toString()),
    ) {
        blocker?.let { issue ->
            LAlert(
                title = issue.check.displayName(),
                description = issue.message,
                variant = LAlertVariant.DANGER,
            )
        }
        rest.forEach { issue ->
            val danger = issue.severity == QaSeverity.ERROR
            Row(
                Modifier
                    .fillMaxWidth()
                    .gap(Space.XS)
                    .fontSize(TypeScale.Small)
                    .color(palette.textSecondary)
                    .attr("data-review-issue", issue.severity.name.lowercase()),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Span(
                    Modifier
                        .color(if (danger) palette.danger else palette.warning)
                        .iconSize(IconSize.SM)
                        .toAttrs { attr("aria-hidden", "true") },
                ) {
                    if (danger) LDangerIcon() else LWarningIcon()
                }
                Span(Modifier.fontWeight(600).toAttrs()) { Text(issue.check.displayName()) }
                Span(Modifier.toAttrs()) { Text(issue.message) }
            }
        }
    }
}

/** One labelled block of stored text, at the weight its role in the decision deserves. */
@Composable
private fun ReviewValuePanel(
    label: String,
    language: String?,
    ground: Color,
    accented: Boolean,
    content: @Composable () -> Unit,
) {
    val palette = ColorMode.current.toSitePalette()
    Column(Modifier.fillMaxWidth().gap(Space.XXS)) {
        Row(Modifier.gap(Space.XS), verticalAlignment = Alignment.CenterVertically) {
            Span(
                Modifier
                    .fontSize(TypeScale.Small)
                    .fontWeight(650)
                    .color(palette.textPrimary)
                    .toAttrs(),
            ) {
                Text(label)
            }
            language?.let {
                Span(
                    Modifier.fontSize(TypeScale.Small).color(palette.textSecondary).toAttrs(),
                ) {
                    Text(it)
                }
            }
        }
        Div(
            Modifier
                .fillMaxWidth()
                .fontSize(TypeScale.Reading)
                .backgroundColor(ground)
                .border(1.px, LineStyle.Solid, palette.border)
                .borderRadius(Radius.Control)
                .padding(Space.MD)
                .then(
                    if (accented) {
                        Modifier.styleModifier {
                            property("border-inline-start", "3px solid ${palette.accent}")
                        }
                    } else {
                        Modifier
                    },
                )
                .toAttrs { attr("data-review-panel", if (accented) "primary" else "recessed") },
        ) {
            content()
        }
    }
}

/**
 * What produced this submission, when it was not a person typing.
 *
 * `HUMAN` gets no badge: it is the overwhelming majority and the default reading. Machine output
 * keeps the warning treatment, because it is the one that must not be skimmed like a colleague's
 * work; a memory hit and an import are facts about provenance rather than warnings.
 */
@Composable
private fun originBadge(origin: TranslationOrigin): Pair<String, LBadgeVariant>? = when (origin) {
    TranslationOrigin.HUMAN -> null
    TranslationOrigin.MACHINE ->
        t("review.review_card.machine_translated", "Machine translated") to LBadgeVariant.WARNING
    TranslationOrigin.MEMORY ->
        t("review.review_card.from_memory", "From memory") to LBadgeVariant.NEUTRAL
    TranslationOrigin.IMPORT ->
        t("review.review_card.imported", "Imported") to LBadgeVariant.NEUTRAL
}

private external fun encodeURIComponent(value: String): String

/**
 * The card that replaces the decision when the string moved while it was being read.
 *
 * Its own surface rather than a banner over the buttons: the choice is between two texts, and
 * offering Approve and Send back beside them would be offering a decision about a version that no
 * longer exists. Approving here re-submits against the version now on screen, so the guard that
 * refused the first attempt is satisfied by the text the reviewer has actually just read.
 */
@Composable
internal fun ReviewStaleConflict(
    item: ReviewItemDto,
    current: TranslationDto,
    locale: String,
    canApprove: Boolean,
    busy: Boolean,
    onApprove: () -> Unit,
    onReload: () -> Unit,
    guardedMode: Boolean = false,
) {
    LConflictCard(
        title = t(
            "review.review_card.this_string_changed_while_you_were",
            "This string changed while you were reading it",
        ),
        description = t(
            "review.review_card.nothing_was_approved_here_is_what",
            "Nothing was approved. Here is what it says now — approve this version, or reload the queue.",
        ),
        mineLabel = t("review.review_card.what_you_were_reading", "What you were reading"),
        theirsLabel = t("review.review_card.what_it_says_now", "What it says now"),
        mine = {
            LocalizedReviewValue(
                locale = locale,
                value = item.targetValue,
                pluralForms = item.targetPluralForms,
                hook = "stale-before",
            )
        },
        theirs = {
            LocalizedReviewValue(
                locale = current.locale.ifBlank { locale },
                value = current.value,
                pluralForms = current.pluralForms,
                hook = "stale-current",
            )
        },
    ) {
        ReviewApprovalButton(
            label = t("review.review_card.approve_this_version", "Approve this version"),
            item = item,
            current = current,
            canApprove = canApprove,
            busy = busy,
            onApprove = onApprove,
            guardedMode = guardedMode,
        )
        LButton(
            t("review.review_card.reload_the_queue", "Reload the queue"),
            onReload,
            variant = LButtonVariant.SECONDARY,
            enabled = !busy,
        )
    }
}
